import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useAnalytics } from "../../store/useAnalytics";
import { SkeletonRows, SkeletonStatCard } from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import NumberFlow from "@number-flow/react";
import type { IReviewPrompt } from "../../store/useAnalytics";


// (no icons / KPI cards – keep the page minimal)

// ─── Event rows config ───────────────────────────────────────────────────────

type EventRow = {
  label: string;
  key: keyof import("../../store/useAnalytics").IEventCounts;
  dot: string;
};

const EVENT_ROWS: EventRow[] = [
  { label: "App opens", key: "app_open", dot: "bg-droppie-oranje" },
  { label: "Login attempts", key: "login_attempt", dot: "bg-droppie-petrol" },
  { label: "Login success", key: "login_success", dot: "bg-primary-lighter" },
  { label: "Login failed", key: "login_failed", dot: "bg-red-base" },
  // Logout is deliberately absent. A re-entrant logout bug wrote ~2M anonymous
  // logout events, so the number says nothing about real usage, and querying it
  // dragged the whole page down. Removed from the backend's event list too.
  {
    label: "Bottom tab clicks",
    key: "bottom_tab_click",
    dot: "bg-droppie-lichtblauw",
  },
  {
    label: "Manual drop details",
    key: "manual_drop_detail_open",
    dot: "bg-droppie-lichtgroen",
  },
  {
    label: "Tomra drop details",
    key: "tomra_drop_detail_open",
    dot: "bg-droppie-petrol",
  },
  {
    label: "Beevision drop details",
    key: "beevision_drop_detail_open",
    dot: "bg-droppie-oranje-dark",
  },
  { label: "Store / location changes", key: "store_change", dot: "bg-yellow" },
  {
    label: "Explore/Rewards screen opens",
    key: "impact_screen_open",
    dot: "bg-droppie-paars",
  },
  {
    label: "Lottery list opens",
    key: "lottery_list_open",
    dot: "bg-droppie-lichtblauw",
  },
  {
    label: "Lottery join success",
    key: "lottery_join_success",
    dot: "bg-primary-lighter",
  },
  { label: "Serious app errors", key: "app_error", dot: "bg-red-base" },
  {
    label: "Home QR button clicks",
    key: "home_button_click",
    dot: "bg-droppie-oranje",
  },
  {
    label: "Review prompts triggered",
    key: "review_prompt_requested",
    dot: "bg-droppie-paars",
  },
  {
    label: "Push notification opens",
    key: "push_notification_opened",
    dot: "bg-droppie-oranje",
  },
  {
    label: "Notifications sheet opens",
    key: "notifications_sheet_open",
    dot: "bg-droppie-petrol",
  },
];

// ─── Section wrapper ─────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="bg-white rounded-xl  overflow-hidden">
    <div className="px-5 py-3">
      <h2 className="font-semibold text-base text-dark">{title}</h2>
    </div>
    {children}
  </div>
);

// ─── Main screen ─────────────────────────────────────────────────────────────

const MobileAnalyticsScreen: React.FC = () => {
  // The pickers edit a draft; only Apply promotes it to the range the queries
  // run on. Fetching straight off the inputs fired seven heavy aggregations per
  // keystroke, which was enough to stall an instance and take the API down with
  // it (App Engine kills an instance that misses its health check).
  const [draftFrom, setDraftFrom] = useState<string>(
    dayjs().subtract(14, "day").format("YYYY-MM-DD")
  );
  const [draftTo, setDraftTo] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [{ from, to }, setAppliedRange] = useState<{
    from: string;
    to: string;
  }>({
    from: dayjs().subtract(14, "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
  });

  const isRangeInvalid =
    !draftFrom || !draftTo || dayjs(draftFrom).isAfter(dayjs(draftTo));
  const isRangeDirty = draftFrom !== from || draftTo !== to;

  const applyRange = () => {
    if (isRangeInvalid) return;
    setAppliedRange({ from: draftFrom, to: draftTo });
  };
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [eventBreakdown, setEventBreakdown] = useState<
    import("../../store/useAnalytics").IEventUserBreakdown[]
  >([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownPage, setBreakdownPage] = useState(1);
  const BREAKDOWN_PAGE_SIZE = 10;
  const REVIEW_PROMPTS_PAGE_SIZE = 20;
  const [reviewPromptsPage, setReviewPromptsPage] = useState(1);

  const {
    summary,
    screens,
    errors,
    eventCounts,
    users,
    reviewPrompts,
    loadingSummary,
    loadingScreens,
    loadingErrors,
    loadingEventCounts,
    loadingUsers,
    loadingReviewPrompts,
    loadSummary,
    loadScreens,
    loadErrors,
    loadEventCounts,
    loadUserSummary,
    loadEventUserBreakdown,
    loadReviewPrompts,
  } = useAnalytics();

  useEffect(() => {
    loadSummary({ from, to });
    loadScreens({ from, to });
    loadErrors({ from, to });
  }, [from, to, loadSummary, loadScreens, loadErrors]);

  useEffect(() => {
    loadEventCounts({ from, to });
    loadUserSummary({ from, to });
    loadReviewPrompts({ from, to });
  }, [from, to, loadEventCounts, loadUserSummary, loadReviewPrompts]);

  const totalAppOpens = summary.reduce((s, p) => s + (p.appOpens || 0), 0);
  const totalSessions = summary.reduce((s, p) => s + (p.sessionsCount || 0), 0);
  const uniqueUsers = summary.reduce(
    (s, p) => s + (p.uniqueUsers > 0 ? 1 : 0),
    0
  );

  const visibleReviewPrompts = useMemo(
    () =>
      (reviewPrompts as IReviewPrompt[]).slice(
        0,
        reviewPromptsPage * REVIEW_PROMPTS_PAGE_SIZE
      ),
    [reviewPrompts, reviewPromptsPage]
  );

  const groupedReviewPrompts = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        total: number;
        lastTimestamp?: string;
        events: IReviewPrompt[];
      }
    >();

    visibleReviewPrompts.forEach((r) => {
      const key =
        (r.email || "").toLowerCase() ||
        `${(r.firstName || "").toLowerCase()} ${(r.lastName || "").toLowerCase()}`.trim() ||
        "unknown";

      const existing = map.get(key);
      const lastTs = existing?.lastTimestamp;
      const isNewer =
        !lastTs || (r.timestamp && dayjs(r.timestamp).isAfter(dayjs(lastTs)));

      const updated: typeof existing = {
        key,
        email: r.email ?? existing?.email,
        firstName: r.firstName ?? existing?.firstName,
        lastName: r.lastName ?? existing?.lastName,
        total: (existing?.total ?? 0) + 1,
        lastTimestamp: isNewer ? r.timestamp : existing?.lastTimestamp,
        events: [...(existing?.events ?? []), r],
      };

      map.set(key, updated as any);
    });

    return Array.from(map.values()).sort(
      (a, b) => (b.total ?? 0) - (a.total ?? 0)
    );
  }, [visibleReviewPrompts]);

  const handleEventClick = async (eventKey: string) => {
    if (expandedEvent === eventKey) {
      setExpandedEvent(null);
      setEventBreakdown([]);
      setBreakdownPage(1);
      return;
    }
    setExpandedEvent(eventKey);
    setBreakdownPage(1);
    setLoadingBreakdown(true);
    const data = await loadEventUserBreakdown({
      eventName: eventKey,
      from,
      to,
    });
    setEventBreakdown(data);
    setLoadingBreakdown(false);
  };

  return (
    <div className="flex flex-col gap-8 w-full max-w-7xl mx-auto pb-12">
      {/* ── Page header ── */}
      <div className="flex flex-col gap-1">
        <h1 className="font-bold text-2xl text-dark">Mobile Analytics</h1>
      </div>

      {/* ── Date filter ── */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-tailgrids-text">
            From
          </label>
          <input
            type="date"
            className="border border-gray-border rounded px-2 py-1 text-sm text-dark bg-white focus:outline-none focus:border-droppie-petrol transition-colors"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-tailgrids-text">To</label>
          <input
            type="date"
            min={draftFrom || undefined}
            className="border border-gray-border rounded px-2 py-1 text-sm text-dark bg-white focus:outline-none focus:border-droppie-petrol transition-colors"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={applyRange}
          disabled={isRangeInvalid || !isRangeDirty}
          className="h-[34px] px-4 rounded font-satoshi text-sm font-medium bg-droppie-oranje text-white disabled:opacity-40"
        >
          Apply
        </button>
        {isRangeDirty && !isRangeInvalid ? (
          <span className="font-satoshi text-xs text-gray-500 self-center">
            Press Apply to load this range
          </span>
        ) : null}
        {isRangeInvalid ? (
          <span className="font-satoshi text-xs text-red-base self-center">
            From must be on or before To
          </span>
        ) : null}
      </div>

      {/* Each panel below owns its own loading state. The endpoints differ a lot
          in speed - the per-user summary is the slowest by a wide margin - so
          gating the whole page on the last one meant staring at a spinner while
          five of six panels already had their data. Panels that are still in
          flight show a skeleton of the same shape, which also stops the page
          from jumping as each one fills in. */}

      {/* ── High level KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {loadingSummary ? (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        ) : (
        <>
        <div className="bg-white rounded-xl  px-4 py-3 flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-tailgrids-text">
            App opens
          </span>
          <span className="text-xl font-semibold text-dark">
            <NumberFlow value={totalAppOpens} />
          </span>
        </div>
        <div className="bg-white rounded-xl  px-4 py-3 flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-tailgrids-text">
            Unique users
          </span>
          <span className="text-xl font-semibold text-dark">
            <NumberFlow value={uniqueUsers} />
          </span>
        </div>
        <div className="bg-white rounded-xl  px-4 py-3 flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-tailgrids-text flex items-center gap-1">
            Sessions
            <span
              className="w-4 h-4 rounded-full border border-gray-border text-[10px] flex items-center justify-center text-tailgrids-text cursor-default"
              title="Number of distinct sessions (unique session IDs) within the selected period."
            >
              i
            </span>
          </span>
          <span className="text-xl font-semibold text-dark">
            <NumberFlow value={totalSessions} />
          </span>
        </div>
        </>
        )}
      </div>

      {/* ── Event counts ── */}
      <Section title="Event Counts">
        {loadingEventCounts ? (
          <SkeletonRows rows={EVENT_ROWS.length} />
        ) : (
        <div className="divide-y divide-gray-border">
          {EVENT_ROWS.map((row) => (
            <div key={row.key}>
              <div
                className="flex items-center justify-between px-5 py-3 hover:bg-sky-lightest transition-colors cursor-pointer"
                onClick={() => handleEventClick(row.key)}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${row.dot}`}
                  />
                  <span className="text-sm text-dark">{row.label}</span>
                  {expandedEvent === row.key && (
                    <span className="text-xs text-tailgrids-text">
                      (click to collapse)
                    </span>
                  )}
                </div>
                <span className="font-semibold text-sm text-droppie-petrol min-w-[40px] text-right">
                  <NumberFlow value={eventCounts[row.key] ?? 0} />
                </span>
              </div>
              {expandedEvent === row.key && (
                <div className="bg-sky-lightest px-5 py-4">
                  {loadingBreakdown ? (
                    <p className="text-sm text-tailgrids-text">Loading...</p>
                  ) : eventBreakdown.length === 0 ? (
                    <p className="text-sm text-tailgrids-text">
                      No user data for this event.
                    </p>
                  ) : (
                    <>
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-border">
                            <th className="text-left py-2 pr-4 text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                              User
                            </th>
                            <th className="text-left py-2 pr-4 text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                              Email
                            </th>
                            <th className="text-right py-2 pr-4 text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                              Count
                            </th>
                            <th className="text-right py-2 text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                              Last seen
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventBreakdown
                            .slice(
                              (breakdownPage - 1) * BREAKDOWN_PAGE_SIZE,
                              breakdownPage * BREAKDOWN_PAGE_SIZE
                            )
                            .map((u, idx) => (
                              <tr
                                key={u.userId || idx}
                                className="border-b border-gray-border last:border-b-0"
                              >
                                <td className="py-2 pr-4 text-dark">
                                  {u.firstName || u.lastName
                                    ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()
                                    : "Unknown"}
                                </td>
                                <td className="py-2 pr-4 text-tailgrids-text">
                                  {u.email ?? "—"}
                                </td>
                                <td className="py-2 pr-4 text-right text-dark font-semibold">
                                  {u.count}
                                </td>
                                <td className="py-2 text-right text-tailgrids-text whitespace-nowrap">
                                  {dayjs(u.lastSeen).format("D MMM HH:mm")}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {eventBreakdown.length > BREAKDOWN_PAGE_SIZE && (
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-border">
                          <span className="text-xs text-tailgrids-text">
                            Showing{" "}
                            {(breakdownPage - 1) * BREAKDOWN_PAGE_SIZE + 1}-
                            {Math.min(
                              breakdownPage * BREAKDOWN_PAGE_SIZE,
                              eventBreakdown.length
                            )}{" "}
                            of {eventBreakdown.length} users
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                setBreakdownPage((p) => Math.max(1, p - 1))
                              }
                              disabled={breakdownPage === 1}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-border bg-white text-dark disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sky-lightest transition-colors"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() =>
                                setBreakdownPage((p) =>
                                  Math.min(
                                    Math.ceil(
                                      eventBreakdown.length /
                                        BREAKDOWN_PAGE_SIZE
                                    ),
                                    p + 1
                                  )
                                )
                              }
                              disabled={
                                breakdownPage >=
                                Math.ceil(
                                  eventBreakdown.length / BREAKDOWN_PAGE_SIZE
                                )
                              }
                              className="px-3 py-1 text-xs rounded-lg border border-gray-border bg-white text-dark disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sky-lightest transition-colors"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </Section>

      {/* ── Screens & errors side by side (responsive) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Screen visits */}
        <Section title="Screen Visits">
          {loadingScreens ? (
            <SkeletonRows rows={6} />
          ) : screens.length === 0 ? (
            <p className="px-5 py-6 text-sm text-tailgrids-text">
              No screen data for this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-sky-lightest">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Screen
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Views
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Unique Users
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-border">
                  {screens.map((s, idx) => (
                    <tr
                      key={s.screen}
                      className={`hover:bg-sky-lightest transition-colors ${
                        idx === 0 ? "font-semibold" : ""
                      }`}
                    >
                      <td className="px-5 py-3 text-sm text-dark">
                        {s.screen}
                      </td>
                      <td className="px-5 py-3 text-sm text-dark text-right">
                        <span className="inline-block bg-droppie-lichtblauw text-droppie-petrol text-xs font-semibold px-2 py-0.5 rounded-full">
                          <NumberFlow value={s.count} />
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-tailgrids-text text-right">
                        <NumberFlow value={s.uniqueUsers} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Serious errors */}
        <Section title="Serious Errors">
          {loadingErrors ? (
            <SkeletonRows rows={6} />
          ) : errors.length === 0 ? (
            <p className="px-5 py-6 text-sm text-tailgrids-text">
              No errors recorded for this period.
            </p>
          ) : (
            <div className="overflow-x-auto ">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-sky-lightest">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Message
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Screen
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Count
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Last Seen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-border">
                  {errors.map((e, idx) => (
                    <tr
                      key={`${e.message}-${idx}`}
                      className="hover:bg-sky-lightest transition-colors"
                    >
                      <td className="px-5 py-3 text-sm text-dark max-w-xs truncate">
                        {e.message}
                      </td>
                      <td className="px-5 py-3 text-sm text-tailgrids-text">
                        {e.screen ?? "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="inline-block bg-red-lightest text-red-base text-xs font-semibold px-2 py-0.5 rounded-full">
                          <NumberFlow value={e.count} />
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-tailgrids-text text-right whitespace-nowrap">
                        {dayjs(e.lastSeen).format("D MMM YYYY HH:mm")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {/* ── Review prompts ── */}
      <Section title="Review prompts triggered">
        {loadingReviewPrompts ? (
          <SkeletonRows rows={5} />
        ) : reviewPrompts.length === 0 ? (
          <p className="px-5 py-6 text-sm text-tailgrids-text">
            No review prompts for this period.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-sky-lightest">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      User
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Email
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Prompts
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                      Last prompt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-border">
                  {groupedReviewPrompts.map((row, idx) => {
                    const fullName =
                      row.firstName || row.lastName
                        ? `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim()
                        : "Unknown user";
                    const tooltipLines = row.events
                      .map((e) => {
                        const dateLabel = e.timestamp
                          ? dayjs(e.timestamp).format("D MMM YYYY HH:mm")
                          : "";
                        const dropLabel =
                          e.dropCount != null ? `drop #${e.dropCount}` : "";
                        const typeLabel = e.dropType ? `(${e.dropType})` : "";
                        return `${dateLabel} ${dropLabel} ${typeLabel}`.trim();
                      })
                      .join("\n");

                    return (
                      <tr
                        key={row.key || idx}
                        className="hover:bg-sky-lightest transition-colors"
                      >
                        <td className="px-5 py-3 text-sm text-dark">
                          <span title={tooltipLines}>{fullName}</span>
                        </td>
                        <td className="px-5 py-3 text-sm text-tailgrids-text">
                          {row.email ?? "—"}
                        </td>
                        <td className="px-5 py-3 text-sm text-dark text-right">
                          <span className="inline-block bg-droppie-petrol/10 text-droppie-petrol text-xs font-semibold px-2 py-0.5 rounded-full">
                            {row.total}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-tailgrids-text text-right whitespace-nowrap">
                          {row.lastTimestamp
                            ? dayjs(row.lastTimestamp).format(
                                "D MMM YYYY HH:mm"
                              )
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {visibleReviewPrompts.length < reviewPrompts.length && (
              <div className="px-5 pb-4">
                <button
                  type="button"
                  className="text-xs font-semibold text-droppie-petrol hover:text-droppie-petrol/80 underline"
                  onClick={() => setReviewPromptsPage((prev) => prev + 1)}
                >
                  Load more (
                  {reviewPrompts.length - visibleReviewPrompts.length}{" "}
                  remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── Per-user activity ── */}
      <Section title="Per-user activity">
        {loadingUsers ? (
          <SkeletonRows rows={8} />
        ) : users.length === 0 ? (
          <p className="px-5 py-6 text-sm text-tailgrids-text">
            No user activity for this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-sky-lightest">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    User
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    Email
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    App opens
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    Screen views
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    Logins
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-tailgrids-text uppercase tracking-wide">
                    Lotteries joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-border">
                {users.map((u, idx) => (
                  <tr
                    key={u.userId || idx}
                    className="hover:bg-sky-lightest transition-colors"
                  >
                    <td className="px-5 py-3 text-sm text-dark">
                      {u.firstName || u.lastName
                        ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()
                        : "Unknown user"}
                    </td>
                    <td className="px-5 py-3 text-sm text-tailgrids-text">
                      {u.email ?? "—"}
                    </td>
                    <td className="px-5 py-3 text-sm text-dark text-right">
                      <NumberFlow value={u.app_open} />
                    </td>
                    <td className="px-5 py-3 text-sm text-dark text-right">
                      <NumberFlow value={u.screen_view} />
                    </td>
                    <td className="px-5 py-3 text-sm text-dark text-right">
                      <NumberFlow value={u.login_success} />
                    </td>
                    <td className="px-5 py-3 text-sm text-dark text-right">
                      <NumberFlow value={u.lottery_join_success} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
};

export default withProtectedRoute(MobileAnalyticsScreen);
